import { useEffect, useRef } from 'react';

import { createPortal } from 'react-dom';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { Box } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { hideToast } from '@/store/slices/toastSlice';

import { TOAST_TYPES } from '../../../components/UIComponents/CommonToast';

import type { ToastType } from './CommonToast';
import CommonToast from './CommonToast';

// Map your existing severity strings → TOAST_TYPES
const SEVERITY_MAP: Record<string, ToastType> = {
  success: TOAST_TYPES.SUCCESS,
  error: TOAST_TYPES.ERROR,
  warning: TOAST_TYPES.WARNING,
  info: TOAST_TYPES.INFO,
};

function ToastItem({ toast, onDismiss }: { toast: any; onDismiss: (id: number) => void }) {
  const reducedMotion = useReducedMotion();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (toast.duration > 0) {
      timerRef.current = setTimeout(() => onDismiss(toast.id), toast.duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast.id, toast.duration, onDismiss]);

  const motionProps = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, x: 28, scale: 0.98 },
        animate: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 0, x: 28, scale: 0.98 },
        transition: { type: 'spring' as const, stiffness: 380, damping: 28 },
      };

  return (
    <motion.div layout={!reducedMotion} {...motionProps} style={{ width: '100%' }}>
      <CommonToast
        type={(SEVERITY_MAP[toast.severity] ?? TOAST_TYPES.INFO) as ToastType}
        message={toast.message}
        onClose={() => onDismiss(toast.id)}
      />
    </motion.div>
  );
}

export function Toast() {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector(state => state.toast.toasts);
  const onDismiss = (id: number) => dispatch(hideToast(id));

  return createPortal(
    <Box
      component="aside"
      aria-label="Notifications"
      sx={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: 'min(400px, calc(100vw - 48px))',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      <AnimatePresence initial={false}>
        {toasts.map((toast: any) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </Box>,
    document.body,
  );
}
